import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, CheckCircle2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, TIME_SLOTS } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  service: "",
  date: "",
  time: "",
  message: "",
};

const inputCls =
  "w-full rounded-2xl border border-brand-green/20 bg-brand-cream/60 px-4 py-3.5 text-brand-ink placeholder:text-brand-mute/60 transition-colors duration-300 focus:border-brand-green focus:bg-white focus:outline-none";

export const BookingForm = ({ selectedService }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedService) setForm((f) => ({ ...f, service: selectedService }));
  }, [selectedService]);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[+]?[0-9\s-]{10,15}$/.test(form.phone.trim()))
      e.phone = "Please enter a valid phone number.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address.";
    if (!form.service) e.service = "Please choose a service.";
    if (!form.date) e.date = "Please pick a preferred date.";
    if (!form.time) e.time = "Please pick a preferred time.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Appointment request received!");
    } catch {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="book" data-testid="booking-section" className="py-24 sm:py-32 bg-brand-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.35fr] gap-14 items-start">
        <Reveal className="lg:sticky lg:top-28">
          <Overline>Reserve Your Chair</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-brand-ink tracking-tight leading-tight">
            Book your
            <span className="italic text-brand-green"> appointment.</span>
          </h2>
          <p className="mt-6 text-brand-mute text-base sm:text-lg leading-relaxed max-w-md">
            Tell us what you'd love done and when — we'll confirm your slot on
            call or WhatsApp within minutes during working hours.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              "Open daily, 8:00 AM – 9:00 PM",
              "Free personalised consultation",
              "Walk-ins always welcome",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-brand-ink">
                <CalendarCheck size={18} className="text-brand-gold shrink-0" aria-hidden="true" />
                <span className="text-sm sm:text-base">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white rounded-[2rem] shadow-lift p-7 sm:p-10 relative overflow-hidden">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  data-testid="booking-success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 18 }}
                  className="py-14 flex flex-col items-center text-center"
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.15 }}
                    className="w-20 h-20 rounded-full bg-brand-green/10 flex items-center justify-center"
                  >
                    <CheckCircle2 size={44} className="text-brand-green" aria-hidden="true" />
                  </motion.span>
                  <h3 className="mt-6 font-serif text-3xl font-bold text-brand-ink">
                    You're on the list!
                  </h3>
                  <p className="mt-3 text-brand-mute max-w-sm leading-relaxed">
                    Thank you, {form.name.split(" ")[0]}. We've received your
                    request for {form.service} and will confirm your slot
                    shortly.
                  </p>
                  <button
                    data-testid="booking-again-button"
                    onClick={() => {
                      setDone(false);
                      setForm(EMPTY);
                    }}
                    className="mt-8 rounded-full border border-brand-green px-7 py-3 text-sm font-semibold text-brand-green transition-colors duration-300 hover:bg-brand-green hover:text-white"
                  >
                    Book Another Appointment
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={submit}
                  noValidate
                  className="grid sm:grid-cols-2 gap-5"
                >
                  <div>
                    <label htmlFor="bk-name" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Name *
                    </label>
                    <input
                      id="bk-name"
                      data-testid="booking-name-input"
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your full name"
                      className={inputCls}
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <p role="alert" data-testid="error-name" className="mt-1.5 text-xs text-red-600">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-phone" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Phone *
                    </label>
                    <input
                      id="bk-phone"
                      data-testid="booking-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 98765 43210"
                      className={inputCls}
                      aria-invalid={!!errors.phone}
                    />
                    {errors.phone && <p role="alert" data-testid="error-phone" className="mt-1.5 text-xs text-red-600">{errors.phone}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="bk-email" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Email
                    </label>
                    <input
                      id="bk-email"
                      data-testid="booking-email-input"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com (optional)"
                      className={inputCls}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <p role="alert" data-testid="error-email" className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="bk-service" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Service *
                    </label>
                    <select
                      id="bk-service"
                      data-testid="booking-service-select"
                      value={form.service}
                      onChange={set("service")}
                      className={inputCls}
                      aria-invalid={!!errors.service}
                    >
                      <option value="">Choose a service…</option>
                      {SERVICES.map((s) => (
                        <option key={s.name} value={s.name}>
                          {s.name} · {s.price} onwards
                        </option>
                      ))}
                    </select>
                    {errors.service && <p role="alert" data-testid="error-service" className="mt-1.5 text-xs text-red-600">{errors.service}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-date" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Preferred Date *
                    </label>
                    <input
                      id="bk-date"
                      data-testid="booking-date-input"
                      type="date"
                      min={today}
                      value={form.date}
                      onChange={set("date")}
                      className={inputCls}
                      aria-invalid={!!errors.date}
                    />
                    {errors.date && <p role="alert" data-testid="error-date" className="mt-1.5 text-xs text-red-600">{errors.date}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-time" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Preferred Time *
                    </label>
                    <select
                      id="bk-time"
                      data-testid="booking-time-select"
                      value={form.time}
                      onChange={set("time")}
                      className={inputCls}
                      aria-invalid={!!errors.time}
                    >
                      <option value="">Choose a time…</option>
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {errors.time && <p role="alert" data-testid="error-time" className="mt-1.5 text-xs text-red-600">{errors.time}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="bk-message" className="block text-sm font-semibold text-brand-ink mb-1.5">
                      Message
                    </label>
                    <textarea
                      id="bk-message"
                      data-testid="booking-message-input"
                      rows={3}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Anything we should know? (optional)"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button
                      data-testid="booking-submit-button"
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-full bg-brand-green px-8 py-4 text-base font-semibold text-white shadow-soft transition-all duration-300 hover:bg-brand-deep hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2.5"
                    >
                      {loading && <Loader2 size={20} className="animate-spin" aria-hidden="true" />}
                      {loading ? "Reserving your slot…" : "Book Appointment"}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
